import React, {useEffect} from 'react';
import $ from 'jquery';
import {compileFormula, orderFormulaColumns, CompiledFormula} from '../utils/calcFormula';


type CalcObjType = { [Key: string]: number | string }
export type CalculationDataType = { [key: number]: { [key: string]: string } };
export type CalculationResultType = { [key: string]: string };

// ── Schema ──────────────────────────────────────────────────────────────────
// A calculation table is now fully described by an ordered list of columns.
// `a`–`p` below is just the default schema (DEFAULT_CALC_SCHEMA) — reproducing
// the previously hardcoded table — so payees / calculations without a stored
// schema keep behaving exactly as before.

export type CalcColKind = 'input' | 'formula';
export type CalcColRole =
    | 'expensesWithVat' | 'incomeWithVat' | 'profit'          // footer totals
    | 'invoiceName' | 'invoiceQty' | 'invoiceUnit'
    | 'invoicePrice' | 'invoiceVat' | 'invoiceTotal';         // PDF product-table sources

export type CalcSchemaColumn = {
    key: string,
    label: string,
    kind: CalcColKind,
    formula?: string,
    group?: 'expenses' | 'income' | null,
    hidden?: boolean,
    inputType?: 'text' | 'number',   // input columns; default 'number'
    decimals?: number,               // formula display precision; default 2
    roles?: CalcColRole[],
};

export const DEFAULT_CALC_SCHEMA: CalcSchemaColumn[] = [
    { key: 'a', label: 'Товар',        kind: 'input',   inputType: 'text',   roles: ['invoiceName'] },
    { key: 'b', label: 'Кол-во',       kind: 'input',   inputType: 'number', roles: ['invoiceQty'] },
    { key: 'c', label: 'Ед.измерения', kind: 'input',   inputType: 'text',   roles: ['invoiceUnit'] },
    { key: 'd', label: 'Цена без ПДВ', kind: 'input',   inputType: 'number', group: 'expenses' },
    { key: 'e', label: 'ПДВ',          kind: 'formula', formula: 'd*b*vat',  group: 'expenses' },
    { key: 'f', label: 'СУММА',        kind: 'formula', formula: 'd*b + e',  group: 'expenses', roles: ['expensesWithVat'] },
    { key: 'g', label: 'Цена без ПДВ', kind: 'input',   inputType: 'number', group: 'income', roles: ['invoicePrice'] },
    { key: 'h', label: 'ПДВ',          kind: 'formula', formula: 'g*b*vat',  group: 'income', roles: ['invoiceVat'] },
    { key: 'i', label: 'СУММА',        kind: 'formula', formula: 'g*b + h',  group: 'income', roles: ['incomeWithVat', 'invoiceTotal'] },
    { key: 'j', label: 'Д-Р(С/С)',     kind: 'formula', formula: 'g*b - d*b' },
    { key: 'k', label: 'ЄП',           kind: 'formula', formula: 'g*b*ep' },
    { key: 'w', label: 'Военный сбор', kind: 'formula', formula: 'g*b*mil' },
    { key: 'l', label: 'ПРИБЫЛЬ',      kind: 'formula', formula: 'j - k - w', roles: ['profit'] },
    { key: 'p', label: '%',            kind: 'formula', formula: 'i == 0 ? 0 : l / i * 100' },
];

// Identifiers a formula may reference besides other column keys.
export const FORMULA_CONSTS = ['vat', 'ep', 'mil'] as const;

const RESERVED_KEYS = new Set<string>([...FORMULA_CONSTS, 'z']);

export function isValidColumnKey(key: string): boolean {
    return /^[a-z][a-z0-9_]{0,15}$/.test(key) && !RESERVED_KEYS.has(key);
}

// Footer field keys get their own (looser) rule: unlike row-column keys they
// aren't typed one-handed in a formula bar next to a–z single letters, so
// mixed case ("incomeTax") and longer names are fine.
export function isValidFooterKey(key: string): boolean {
    return /^[a-zA-Z][a-zA-Z0-9_]{0,31}$/.test(key) && !RESERVED_KEYS.has(key);
}

// ── Payee-scoped config ─────────────────────────────────────────────────────

export type CalcColumnCfg = { label?: string, hidden?: boolean };
export type CalcColumnsCfg = { [key: string]: CalcColumnCfg };

// ── Footer (summary) fields ─────────────────────────────────────────────────
// The row under the table ("Сумма расхода с ПДВ", "Прибыль", "Маржа", …).
// `sum` totals one row column across every row; `formula` computes an
// expression over other footer keys (declared earlier in the list) plus the
// rate constants — same engine as row formulas, just evaluated once over
// totals instead of per row.
export type CalcFooterKind = 'sum' | 'formula';
export type CalcFooterField = {
    key: string,
    label: string,
    kind: CalcFooterKind,
    column?: string,      // kind='sum': which row-schema column to total
    formula?: string,     // kind='formula': expression over other footer keys + vat/ep/mil
    decimals?: number,    // default 2
    suffix?: string,      // appended after the number, e.g. '%'
    color?: string,       // '#rrggbb' text color override; default when unset
};

export type CalcConfig = {
    vatRate: number,
    epRate: number,
    militaryRate: number,
    columns?: CalcColumnsCfg,          // legacy (Phase 2) label / visibility overrides
    schema?: CalcSchemaColumn[],       // Phase 3 full column schema
    footer?: CalcFooterField[],        // Phase 4 footer/summary fields
    // Not part of the stored config: CalcTable.hiddenColumns layered on by
    // `configOfCalcTable` — when set, it replaces the schema's `hidden` flags.
    hiddenColumns?: string[],
};
export const DEFAULT_CALC_CONFIG: CalcConfig = { vatRate: 20, epRate: 3, militaryRate: 1 };

const clampPct = (n: number, fallback: number): number =>
    Number.isFinite(n) && n >= 0 && n <= 100 ? n : fallback;

export function normalizeCalcConfig(raw: any): CalcConfig {
    const src = raw && typeof raw === 'object' ? raw : {};
    const out: CalcConfig = {
        vatRate: clampPct(Number(src.vatRate), DEFAULT_CALC_CONFIG.vatRate),
        epRate: clampPct(Number(src.epRate), DEFAULT_CALC_CONFIG.epRate),
        militaryRate: clampPct(Number(src.militaryRate), DEFAULT_CALC_CONFIG.militaryRate),
    };

    if (src.columns && typeof src.columns === 'object') {
        const cols: CalcColumnsCfg = {};
        for (const [k, v] of Object.entries(src.columns as any)) {
            if (!v || typeof v !== 'object') continue;
            const entry: CalcColumnCfg = {};
            const label = typeof (v as any).label === 'string' ? (v as any).label.trim() : '';
            if (label) entry.label = label.slice(0, 64);
            if ((v as any).hidden === true || (v as any).hidden === 'true') entry.hidden = true;
            if (entry.label !== undefined || entry.hidden) cols[k] = entry;
        }
        if (Object.keys(cols).length) out.columns = cols;
    }

    const schema = normalizeSchema(src.schema);
    if (schema) out.schema = schema;

    const footer = normalizeFooter(src.footer);
    if (footer) out.footer = footer;

    if (Array.isArray(src.hiddenColumns)) {
        out.hiddenColumns = src.hiddenColumns.filter((k: any) => typeof k === 'string');
    }

    return out;
}

// A CalcTable's frozen `config` plus its always-editable `hiddenColumns`
// (admin-chosen column visibility, changeable even once the table is used).
// Undefined when there's no table, so callers can `??` onto legacy configs.
export function configOfCalcTable(table?: { config?: any, hiddenColumns?: string[] | null } | null): any {
    if (!table?.config) return undefined;
    return Array.isArray(table.hiddenColumns) ? { ...table.config, hiddenColumns: table.hiddenColumns } : table.config;
}

// Keep only well-formed columns; drop a schema that ends up empty.
export function normalizeSchema(raw: any): CalcSchemaColumn[] | undefined {
    if (!Array.isArray(raw)) return undefined;
    const seen = new Set<string>();
    const out: CalcSchemaColumn[] = [];
    for (const c of raw) {
        if (!c || typeof c !== 'object') continue;
        const key = `${c.key ?? ''}`.trim();
        if (!isValidColumnKey(key) || seen.has(key)) continue;
        const kind: CalcColKind = c.kind === 'formula' ? 'formula' : 'input';
        const col: CalcSchemaColumn = {
            key,
            label: `${c.label ?? ''}`.trim().slice(0, 64) || key,
            kind,
        };
        if (kind === 'formula') col.formula = `${c.formula ?? ''}`.slice(0, 512);
        else col.inputType = c.inputType === 'text' ? 'text' : 'number';
        if (c.group === 'expenses' || c.group === 'income') col.group = c.group;
        if (c.hidden === true) col.hidden = true;
        if (Number.isFinite(Number(c.decimals))) col.decimals = Math.max(0, Math.min(6, Math.trunc(Number(c.decimals))));
        if (Array.isArray(c.roles)) {
            const roles = c.roles.filter((r: any) => typeof r === 'string');
            if (roles.length) col.roles = roles;
        }
        seen.add(key);
        out.push(col);
    }
    return out.length ? out : undefined;
}

// Human-readable problems with a schema (empty = OK): formula syntax, unknown
// column references, dependency cycles. Mirrors the server's validateSchema.
export function validateSchema(schema: CalcSchemaColumn[]): string[] {
    const problems: string[] = [];
    const keys = new Set(schema.map((c) => c.key));
    const inputs = new Set(schema.filter((c) => c.kind === 'input').map((c) => c.key));
    const formulaCols: Array<{ key: string, refs: string[] }> = [];

    for (const c of schema) {
        if (c.kind !== 'formula') continue;
        const res = compileFormula(c.formula || '');
        if (!res.ok) { problems.push(`«${c.label}» (${c.key}): ${res.error}`); continue; }
        for (const r of res.compiled.refs) {
            if (!keys.has(r)) problems.push(`«${c.label}» (${c.key}): неизвестный столбец "${r}"`);
        }
        formulaCols.push({ key: c.key, refs: res.compiled.refs });
    }

    const available = new Set<string>(Array.from(inputs).concat(FORMULA_CONSTS as unknown as string[]));
    const { cyclic } = orderFormulaColumns(formulaCols, available);
    if (cyclic.length) problems.push(`Циклическая зависимость формул: ${cyclic.join(', ')}`);
    return problems;
}

// Legacy Phase-2 `columns` overrides layered onto the default schema.
function schemaFromColumnOverrides(columns: CalcColumnsCfg): CalcSchemaColumn[] {
    return DEFAULT_CALC_SCHEMA.map((c) => {
        const o = columns[c.key];
        if (!o) return { ...c };
        return { ...c, label: o.label || c.label, hidden: o.hidden ? true : c.hidden };
    });
}

function getBaseSchema(config?: CalcConfig | null): CalcSchemaColumn[] {
    if (config?.schema && config.schema.length) return config.schema;
    if (config?.columns && Object.keys(config.columns).length) return schemaFromColumnOverrides(config.columns);
    return DEFAULT_CALC_SCHEMA;
}

export function getEffectiveSchema(config?: CalcConfig | null): CalcSchemaColumn[] {
    const schema = getBaseSchema(config);
    if (!config?.hiddenColumns) return schema;
    const hidden = new Set(config.hiddenColumns);
    return schema.map((c) => ({ ...c, hidden: hidden.has(c.key) || undefined }));
}

// Keep only well-formed footer fields; drop one that ends up empty.
export function normalizeFooter(raw: any): CalcFooterField[] | undefined {
    if (!Array.isArray(raw)) return undefined;
    const seen = new Set<string>();
    const out: CalcFooterField[] = [];
    for (const f of raw) {
        if (!f || typeof f !== 'object') continue;
        const key = `${f.key ?? ''}`.trim();
        if (!isValidFooterKey(key) || seen.has(key)) continue;
        const kind: CalcFooterKind = f.kind === 'formula' ? 'formula' : 'sum';
        const field: CalcFooterField = {
            key,
            label: `${f.label ?? ''}`.trim().slice(0, 64) || key,
            kind,
        };
        if (kind === 'sum') field.column = `${f.column ?? ''}`.trim();
        else field.formula = `${f.formula ?? ''}`.slice(0, 512);
        if (Number.isFinite(Number(f.decimals))) field.decimals = Math.max(0, Math.min(6, Math.trunc(Number(f.decimals))));
        const suffix = typeof f.suffix === 'string' ? f.suffix.trim().slice(0, 16) : '';
        if (suffix) field.suffix = suffix;
        const color = typeof f.color === 'string' ? f.color.trim() : '';
        if (/^#[0-9a-fA-F]{6}$/.test(color)) field.color = color;
        seen.add(key);
        out.push(field);
    }
    return out.length ? out : undefined;
}

// Human-readable problems with a footer list (empty = OK): a `sum` field must
// point at a real row column; a `formula` field's formula must compile and
// only reference earlier footer keys / rate constants; no dependency cycles.
export function validateFooter(schema: CalcSchemaColumn[], footer: CalcFooterField[]): string[] {
    const problems: string[] = [];
    const colKeys = new Set(schema.map((c) => c.key));
    const footerKeys = new Set(footer.map((f) => f.key));
    const formulaFields: Array<{ key: string, refs: string[] }> = [];

    for (const f of footer) {
        if (f.kind === 'sum') {
            if (!f.column || !colKeys.has(f.column)) {
                problems.push(`«${f.label}» (${f.key}): не выбрана колонка для суммирования`);
            }
        } else {
            const res = compileFormula(f.formula || '');
            if (!res.ok) { problems.push(`«${f.label}» (${f.key}): ${res.error}`); continue; }
            for (const r of res.compiled.refs) {
                if (!footerKeys.has(r) && !(FORMULA_CONSTS as readonly string[]).includes(r)) {
                    problems.push(`«${f.label}» (${f.key}): неизвестное поле "${r}"`);
                }
            }
            formulaFields.push({ key: f.key, refs: res.compiled.refs });
        }
    }

    const available = new Set<string>([
        ...footer.filter((f) => f.kind === 'sum').map((f) => f.key),
        ...FORMULA_CONSTS as unknown as string[],
    ]);
    const { cyclic } = orderFormulaColumns(formulaFields, available);
    if (cyclic.length) problems.push(`Циклическая зависимость итоговых полей: ${cyclic.join(', ')}`);
    return problems;
}

// Explicit `footer` wins; otherwise derive the legacy 4-line footer from
// whichever columns carry the old total roles, so tables that predate this
// feature (or never set one) keep showing exactly what they used to.
export function getEffectiveFooter(config: CalcConfig | null | undefined, schema: CalcSchemaColumn[]): CalcFooterField[] {
    if (config?.footer && config.footer.length) return config.footer;

    const byRole = (role: CalcColRole) => schema.find((c) => (c.roles || []).includes(role));
    const expensesCol = byRole('expensesWithVat');
    const incomeCol = byRole('incomeWithVat');
    const profitCol = byRole('profit');

    const footer: CalcFooterField[] = [];
    if (expensesCol) footer.push({ key: 'expensesTax', label: 'Сумма расхода с ПДВ', kind: 'sum', column: expensesCol.key });
    if (incomeCol) footer.push({ key: 'incomeTax', label: 'Сумма дохода с ПДВ', kind: 'sum', column: incomeCol.key });
    if (profitCol) footer.push({ key: 'profit', label: 'Прибыль', kind: 'sum', column: profitCol.key });
    if (incomeCol && profitCol) {
        footer.push({
            key: 'margin', label: 'Маржа', kind: 'formula',
            formula: 'incomeTax == 0 ? 0 : profit / incomeTax * 100', suffix: '%',
        });
    }
    return footer;
}

// ── Per-Key module state ────────────────────────────────────────────────────
// The table's DOM build + math run in the free functions below, so config /
// compiled formulas / render bookkeeping live at module scope keyed by `Key`.

const calcConfigByKey: { [key: string]: CalcConfig } = {};
const getCalcConfig = (Key: string): CalcConfig => calcConfigByKey[Key] ?? DEFAULT_CALC_CONFIG;
const getSchema = (Key: string): CalcSchemaColumn[] => getEffectiveSchema(getCalcConfig(Key));
const getFooter = (Key: string): CalcFooterField[] => getEffectiveFooter(getCalcConfig(Key), getSchema(Key));

const renderedSigByKey: { [key: string]: string } = {};
const editableByKey: { [key: string]: boolean } = {};
const initialDataByKey: { [key: string]: { [Key: number]: CalcObjType } | undefined } = {};

const HIDDEN_COL_CLASS = 'cmpny__calc-col-hidden';

const schemaSig = (schema: CalcSchemaColumn[]): string => JSON.stringify(schema);

type CompiledSchema = {
    sig: string,
    inputKeys: string[],
    formulaOrder: Array<{ key: string, compiled: CompiledFormula, decimals: number }>,
    cyclic: Set<string>,
};
const compiledByKey: { [key: string]: CompiledSchema } = {};

function getCompiled(Key: string): CompiledSchema {
    const schema = getSchema(Key);
    const sig = schemaSig(schema);
    if (compiledByKey[Key]?.sig === sig) return compiledByKey[Key];

    const inputKeys = schema.filter((c) => c.kind === 'input').map((c) => c.key);
    const compiledCols: Array<{ key: string, compiled: CompiledFormula, refs: string[], decimals: number }> = [];
    for (const c of schema) {
        if (c.kind !== 'formula') continue;
        const res = compileFormula(c.formula || '0');
        const compiled = res.ok ? res.compiled : { refs: [], eval: () => 0 };
        compiledCols.push({
            key: c.key,
            compiled,
            refs: compiled.refs,
            decimals: c.decimals ?? 2,
        });
    }

    const available = new Set<string>(inputKeys.concat(FORMULA_CONSTS as unknown as string[]));
    const { order, cyclic } = orderFormulaColumns(
        compiledCols.map((c) => ({ key: c.key, refs: c.refs })),
        available,
    );
    const byKey = new Map<string, typeof compiledCols[number]>(
        compiledCols.map((c) => [c.key, c] as [string, typeof compiledCols[number]]),
    );
    const formulaOrder = order
        .map((k) => byKey.get(k))
        .filter((c): c is typeof compiledCols[number] => !!c)
        .map((c) => ({ key: c.key, compiled: c.compiled, decimals: c.decimals }));

    const out: CompiledSchema = { sig, inputKeys, formulaOrder, cyclic: new Set(cyclic) };
    compiledByKey[Key] = out;
    return out;
}

// ── Component ───────────────────────────────────────────────────────────────

const CalculationTable = (props: {
    isEditable: boolean,
    Key: string,
    tableData?: { [Key: number]: CalcObjType },
    calcConfig?: CalcConfig,
}) => {

    calcConfigByKey[props.Key] = props.calcConfig ? normalizeCalcConfig(props.calcConfig) : DEFAULT_CALC_CONFIG;

    useEffect(() => {
        initialDataByKey[props.Key] = props.tableData;
        renderTable({ Key: props.Key, isEditable: props.isEditable, forceData: props.tableData });
        return () => {
            delete renderedSigByKey[props.Key];
            delete compiledByKey[props.Key];
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Schema / rates usually arrive after the first render (async payee fetch).
    // Rebuild from the new schema, carrying over whatever is already typed in.
    useEffect(() => {
        renderTable({ Key: props.Key, isEditable: editableByKey[props.Key] ?? props.isEditable });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.calcConfig]);

    return (
        <div id={`${props.Key}`} className="cmpny__calculation-table-container"></div>
    );
};


// Build (or rebuild) the whole table. Cheap no-op when the schema in the DOM
// already matches and no explicit data was handed in.
function renderTable(params: { Key: string, isEditable?: boolean, forceData?: { [Key: number]: CalcObjType } }) {
    const Key = params.Key;
    const sig = schemaSig(getSchema(Key)) + '::' + JSON.stringify(getFooter(Key));
    const exists = $(`#${Key}Table`).length > 0;

    if (exists && renderedSigByKey[Key] === sig && params.forceData === undefined) {
        return;
    }

    const data = params.forceData !== undefined
        ? params.forceData
        : exists
            ? gatherTableData({ Key }).tableData
            : initialDataByKey[Key];

    const isEditable = params.isEditable ?? editableByKey[Key] ?? false;

    addTable({ Key });
    addFooter({ Key });
    fillUpTable({ Key, tableData: data && Object.keys(data).length ? data : undefined });
    setTableState({ Key, isEditable });
    watchEnteredData({ Key });
    recalcAllRows({ Key });
    renderedSigByKey[Key] = sig;
}


function addTable(params: { Key: string }) {
    const Key = params.Key;
    const schema = getSchema(Key);
    const container = $(`#${Key}`);
    container.empty();

    // Merged Расход / Доход row: one <th> for the control column + leading
    // ungrouped columns, then one per run of same-group columns.
    const groupCells: string[] = [];
    let lead = 1; // control column
    let idx = 0;
    while (idx < schema.length && !schema[idx].group) { lead++; idx++; }
    groupCells.push(`<th id="${Key}-cgrp-0" colspan="${lead}"></th>`);
    let grp = 1;
    while (idx < schema.length) {
        const g = schema[idx].group;
        let span = 0;
        while (idx < schema.length && schema[idx].group === g) { span++; idx++; }
        if (g) {
            const cls = g === 'expenses' ? 'cmpny__table-expenses cmpny__expenses-color'
                : 'cmpny__table-income cmpny__income-color';
            const label = g === 'expenses' ? 'Расход' : 'Доход';
            groupCells.push(`<th id="${Key}-cgrp-${grp++}" class="${cls}" colspan="${span}">${label}</th>`);
        } else {
            groupCells.push(`<th id="${Key}-cgrp-${grp++}" colspan="${span}"></th>`);
        }
    }

    const headCells = schema.map((c) => {
        const tint = c.group === 'expenses' ? ' cmpny__expenses-color'
            : c.group === 'income' ? ' cmpny__income-color' : '';
        const hidden = c.hidden ? ` ${HIDDEN_COL_CLASS}` : '';
        return `<th id="${Key}-cth-${c.key}" class="${Key}-cth cth-${c.key}${tint}${hidden}" colspan="1">${escapeHtml(c.label)}</th>`;
    }).join('');

    container.append(`
            <table id="${Key}Table" class="cmpny__calculation-table">
                <thead>
                <tr>${groupCells.join('')}</tr>
                <tr>
                    <th id="addClmn-${Key}" class="cmpny__calculation-new-clmn" colspan="1"></th>
                    ${headCells}
                </tr>
                </thead>
                <tbody id="${Key}Tbody" class="cmpny__calculation-tbody"></tbody>
            </table>

    `);
}

// Builds the footer/summary rows into a container the host page renders as
// `<div id={`${Key}-footer`}>` next to the table (see SelectedCompany /
// CalculationsContainer). One `<span>` per field, populated by `text()`
// updates in `calculateTotalSum` — rebuilt only when the field list itself
// changes (gated by `renderTable`'s signature), not on every recalculation.
function addFooter(params: { Key: string }) {
    const Key = params.Key;
    const container = $(`#${Key}-footer`);
    if (!container.length) return;
    container.empty();
    for (const f of getFooter(Key)) {
        const style = f.color ? ` style="color:${f.color}"` : '';
        container.append(`<span id="${Key}-footer-${f.key}" class="cmpny__calc-footer-row"${style}></span>`);
    }
}


function cellHtml(Key: string, colIdx: number, id: string | number, col: CalcSchemaColumn, value: string): string {
    const tint = col.group === 'expenses' ? ' cmpny__expenses-color'
        : col.group === 'income' ? ' cmpny__income-color' : '';
    const hidden = col.hidden ? ` ${HIDDEN_COL_CLASS}` : '';
    return `<td id="${Key}-${col.key}-${id}" class="cmpny__table-field${tint}${hidden} ${Key}-ctf-${col.key} ctf-${col.key}">${escapeHtml(value)}</td>`;
}

function rowHtml(Key: string, id: string | number, data?: { [k: string]: string }): string {
    const schema = getSchema(Key);
    const cells = schema.map((c, i) => {
        let v = data ? (data[c.key] ?? '') : '';
        if (!data && c.kind === 'formula') v = (0).toFixed(c.decimals ?? 2);
        return cellHtml(Key, i, id, c, `${v}`);
    }).join('');
    return `
        <tr id="${Key}-tr-${id}" class="cmpny__table-tr-${Key}">
            <td id="${Key}-z-${id}" class="cmpny__table-field cmpny__table-del-field-${Key} ${Key}-ctf-z ctf-z"></td>
            ${cells}
        </tr>`;
}

export function fillUpTable(params: { Key: string, tableData?: { [Key: number]: CalcObjType } }) {
    const Key = params.Key;
    const tableData = params.tableData;
    const tbody = $(`#${Key}Tbody`);
    tbody.empty();

    if (!tableData || Object.keys(tableData).length === 0) {
        tbody.append(rowHtml(Key, 0));
        return;
    }

    const rows = Object.entries(tableData).map(([id, data]) => rowHtml(Key, id, data as { [k: string]: string }));
    tbody.append(rows.join(''));
}

export function setTableState(params: { Key: string, isEditable: boolean }) {
    const Key = params.Key;
    const isEditable = params.isEditable;
    editableByKey[Key] = isEditable;

    const schema = getSchema(Key);
    const inputSelector = schema
        .filter((c) => c.kind === 'input')
        .map((c) => `.${Key}-ctf-${c.key}`)
        .join(', ');

    const newClmnBtnContainer = $(`#addClmn-${Key}`);
    const delClmnBtnContainer = $(`.cmpny__table-del-field-${Key}`);

    if (isEditable) {
        newClmnBtnContainer.empty();
        newClmnBtnContainer.append(`<button class="cmpny__table-addFiled-btn cmpny__table-addFiled-btn-${Key}">&#43;</button>`);

        delClmnBtnContainer.empty();
        delClmnBtnContainer.append(`<button class="cmpny__table-delFiled-btn cmpny__table-delFiled-btn-${Key}">&#215;</button>`);

        $(document).off('click', `.cmpny__table-addFiled-btn-${Key}`);
        $(document).on('click', `.cmpny__table-addFiled-btn-${Key}`, function () {
            handleAddField({ Key: Key });
        });
        $(document).off('click', `.cmpny__table-delFiled-btn-${Key}`);
        $(document).on('click', `.cmpny__table-delFiled-btn-${Key}`, function (e) {
            const elId = `${$(e.target).parent()[0].id}`.split('-').pop();
            handleDeleteField({ elId: elId, Key: Key });
        });

        if (inputSelector) $(inputSelector).attr('contenteditable', 'true').addClass('cmpny__field-editable');
    } else {
        newClmnBtnContainer.empty();
        delClmnBtnContainer.empty();
        $(document).off('click', `.cmpny__table-addFiled-btn-${Key}`);
        $(document).off('click', `.cmpny__table-delFiled-btn-${Key}`);

        if (inputSelector) $(inputSelector).attr('contenteditable', 'false').removeClass('cmpny__field-editable');
    }

    calculateTotalSum({ Key: Key });
}

function handleDeleteField(params: { elId: string | undefined, Key: string }) {
    if (params.elId) {
        $(`#${params.Key}-tr-${params.elId}`).remove();
        calculateTotalSum({ Key: params.Key });
    }
}

function handleAddField(params: { Key: string }) {
    const Key = params.Key;
    const lastItem = $(`.cmpny__table-tr-${Key}`).last()[0];

    let id = 0;
    try {
        // @ts-ignore
        id = parseInt(lastItem.id.split('-').pop()) + 1;
    } catch (err) {
        id = 0;
    }

    $(`#${Key}Tbody`).append(rowHtml(Key, id));

    // The new row's delete button + contenteditable flags come from setTableState.
    const schema = getSchema(Key);
    $(`#${Key}-z-${id}`).append(`<button class="cmpny__table-delFiled-btn cmpny__table-delFiled-btn-${Key}">&#215;</button>`);
    for (const c of schema) {
        if (c.kind === 'input') {
            $(`#${Key}-${c.key}-${id}`).attr('contenteditable', 'true').addClass('cmpny__field-editable');
        }
    }
}

function watchEnteredData(params: { Key: string }) {
    const Key = params.Key;
    const schema = getSchema(Key);
    for (const c of schema) {
        if (c.kind !== 'input') continue;
        $(document).off('input', `.${Key}-ctf-${c.key}`);
        $(document).on('input', `.${Key}-ctf-${c.key}`, function (e) {
            const id = (e.target as HTMLElement).id.split('-')[2];
            recalcRow({ Key: Key, id: id });
        });
    }
}

const toNum = (s: string | undefined): number => {
    const n = parseFloat(`${s ?? ''}`.replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
};

function recalcRow(params: { Key: string, id: string | number }) {
    const Key = params.Key;
    const id = params.id;
    const cfg = getCalcConfig(Key);
    const { inputKeys, formulaOrder, cyclic } = getCompiled(Key);

    const ctx: { [k: string]: number } = {
        vat: cfg.vatRate / 100,
        ep: cfg.epRate / 100,
        mil: cfg.militaryRate / 100,
    };
    for (const k of inputKeys) ctx[k] = toNum($(`#${Key}-${k}-${id}`).text());

    for (const f of formulaOrder) {
        const val = f.compiled.eval(ctx);
        ctx[f.key] = val;
        $(`#${Key}-${f.key}-${id}`).text(val.toFixed(f.decimals));
    }
    cyclic.forEach((k) => {
        ctx[k] = 0;
        $(`#${Key}-${k}-${id}`).text('ERR');
    });

    calculateTotalSum({ Key: Key });
}

// Exported so a host page can force a recompute when only the rates changed
// (a schema-signature-gated rebuild wouldn't otherwise notice — see
// `renderTable`'s `sig`, which only covers schema + footer shape).
export function recalcAllRows(params: { Key: string }) {
    const Key = params.Key;
    $(`.cmpny__table-tr-${Key}`).each((_, el) => {
        const id = el.id.split('-').pop();
        if (id !== undefined) recalcRow({ Key: Key, id: id });
    });
    calculateTotalSum({ Key: Key });
}

function sumColumn(Key: string, columnKey: string): number {
    let total = 0;
    $(`.${Key}-ctf-${columnKey}`).each((_, el) => {
        const n = parseFloat(($(el).text() || '').replace(',', '.'));
        if (Number.isFinite(n)) total += n;
    });
    return total;
}

// Evaluates every footer field (sums first, then formulas in dependency
// order) and writes each one's display text into its `#${Key}-footer-${key}`
// span. Mirrors `recalcRow`, just over column totals instead of one row.
function calculateTotalSum(params: { Key: string }) {
    const Key = params.Key;
    const footer = getFooter(Key);
    const cfg = getCalcConfig(Key);

    const ctx: { [k: string]: number } = {
        vat: cfg.vatRate / 100,
        ep: cfg.epRate / 100,
        mil: cfg.militaryRate / 100,
    };

    const sumFields = footer.filter((f) => f.kind === 'sum');
    for (const f of sumFields) ctx[f.key] = f.column ? sumColumn(Key, f.column) : 0;

    const formulaFields = footer.filter((f) => f.kind === 'formula');
    const compiled = formulaFields.map((f) => ({ key: f.key, res: compileFormula(f.formula || '0') }));
    const available = new Set<string>([...sumFields.map((f) => f.key), ...FORMULA_CONSTS as unknown as string[]]);
    const { order } = orderFormulaColumns(
        compiled.map((c) => ({ key: c.key, refs: c.res.ok ? c.res.compiled.refs : [] })),
        available,
    );
    const byKey = new Map(compiled.map((c) => [c.key, c] as const));
    for (const k of order) {
        const c = byKey.get(k);
        if (!c) continue;
        ctx[k] = c.res.ok ? c.res.compiled.eval(ctx) : 0;
    }

    for (const f of footer) {
        const val = ctx[f.key] ?? 0;
        $(`#${Key}-footer-${f.key}`).text(`${f.label}: ${val.toFixed(f.decimals ?? 2)}${f.suffix ?? ''}`);
    }
}

export function gatherTableData(params: { Key: string }) {
    const Key = params.Key;
    const schema = getSchema(Key);
    const inputKeys = schema.filter((c) => c.kind === 'input').map((c) => c.key);

    const tableDataObj: CalculationDataType = {};
    const resultDataObj: CalculationResultType = {};
    for (const f of getFooter(Key)) {
        resultDataObj[f.key] = text(`#${Key}-footer-${f.key}`);
    }

    const rows = $(`.cmpny__table-tr-${Key}`).toArray();
    let outIdx = 0;
    for (const row of rows) {
        const id = row.id.split('-').pop();
        const rowObj: { [k: string]: string } = {};
        let hasInput = false;
        for (const c of schema) {
            const t = ($(`#${Key}-${c.key}-${id}`).text() || '').trim();
            rowObj[c.key] = t;
            if (inputKeys.includes(c.key) && t.length !== 0) hasInput = true;
        }
        if (hasInput) tableDataObj[outIdx++] = rowObj;
    }

    return { tableData: tableDataObj, tableResult: resultDataObj };
}

function text(sel: string): string {
    const el = $(sel)[0] as HTMLElement | undefined;
    return el ? el.innerText : '';
}

function escapeHtml(s: string): string {
    return `${s ?? ''}`
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}


export default CalculationTable;
